import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Database,
  Layers
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { usePreview } from '../hooks/usePreview';
import { parcoursService } from '../services/parcoursService';
import { usePesaParcours } from '../stores/usePesaParcours';

import AssetOverlay from '../components/MasterParcours/AssetOverlay';
import LearningArea from '../components/MasterParcours/LearningArea';
import MobileLayout from '../components/MasterParcours/MobileLayout';
import NeuralNarrator from '../components/MasterParcours/NeuralNarrator';
import ResourcePanel from '../components/MasterParcours/ResourcePanel';
import SidebarNavigation from '../components/MasterParcours/SidebarNavigation';
import WorkspaceHeader from '../components/MasterParcours/WorkspaceHeader';
import SignalVisualizer from '../components/SignalVisualizer';



const ParcoursPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { setCapstoneFocus } = usePesaParcours();
  const [searchParams] = useSearchParams();
  
  const [isNavOpen, setIsNavOpen] = useState(true);
  const [isAssetsOpen, setIsAssetsOpen] = useState(true);
  const { isPreview, previewData } = usePreview();
  const [isLoading, setIsLoading] = useState(true);
  const [embeddedMedia, setEmbeddedMedia] = useState(null);

  const { 
    initParcours,
    modules,
    courseData, 
    activeInvestigation,
    currentModule,
    progress,
    focusResource,
    activeItem,
    toggleShowNarrator,
    ui,
    closeNarrator,
    resolveAsset,
    validateGranule
  } = usePesaParcours();

  const showNarrator = ui.showNarrator;  
  const currentMediators = 
    activeItem?.data?.mediators ||
    activeItem?.data?.mediatorOnStart || 
    currentModule?.mediators || 
    [];

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        if (isPreview && previewData) {
          console.log("SYSTEM STATUS: Protection en cours...");
          initParcours(previewData, {}); 
          setIsLoading(false);
          return;
        }

        if (!slug) return;
        const response = await parcoursService.getInvestigation(slug);
        if (response?.course) {
          initParcours(response.course, response.progress || {});
        }
      } catch (error) {
        console.error("OS CRITICAL ERROR:", error);
        if (!isPreview) navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [slug, isPreview, previewData, initParcours, navigate]);

  useEffect(() => {
    // Fallback uniquement si aucune investigation active ET aucun item actif
    if (isLoading || !modules?.length || activeItem || activeInvestigation?.id) return;
    // Si on est vraiment au tout début (pas de progression sauvegardée)
    const firstModule = modules[0];
    if (firstModule?.granules?.length > 0) {
      focusResource(firstModule.granules[0].id);
    }
  }, [isLoading, modules, activeItem, activeInvestigation?.id, focusResource]);

  useEffect(() => {
    const handleGranuleMessage = (event) => {
      if (event.data?.type === 'PESA SIMULATION FINISHED') {
        const { results } = event.data;
        validateGranule(activeItem.id, {
          pesaGain: results.score,
          reflection: results.reflection,
          isSuccess: results.isSuccess
        });
        toast.success("Progression synchronisée avec le Master Engine");
      }
    };

    window.addEventListener('message', handleGranuleMessage);
    return () => window.removeEventListener('message', handleGranuleMessage);
  }, [activeItem?.id, validateGranule]);

  const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
    useEffect(() => {
      const handler = () => setIsMobile(window.innerWidth < 1024);
      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
  };
  
  const isMobile = useIsMobile();
  if (isLoading) return <SystemBootLoader darkMode={darkMode} />;

  if (!isLoading && isMobile) {
    return (
      <MobileLayout
        courseData={courseData}
        progress={progress}
        activeItem={activeItem}
        currentModule={currentModule}
        currentMediators={currentMediators}
        resolveAsset={resolveAsset}
        closeNarrator={closeNarrator}
        showNarrator={showNarrator}
        toggleShowNarrator={toggleShowNarrator}
      />
    );
  }

  return (
    <div className={`h-screen w-screen flex flex-col overflow-hidden transition-all duration-700 ${
      darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50'
    }`}>
      
      {/* GRID PATTERN BACKGROUND */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className={`absolute inset-0 ${
          darkMode 
            ? 'bg-[linear-gradient(rgba(59,130,246,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,0.03) 1px,transparent 1px)]' 
            : 'bg-[linear-gradient(rgba(148,163,184,0.1) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,0.1) 1px,transparent 1px)]'
        }`} style={{ backgroundSize: '40px 40px' }} />
        <div className={`absolute top-0 left-0 w-96 h-96 ${darkMode ? 'bg-blue-600/5' : 'bg-blue-400/10'} blur-[120px] rounded-full`} />
        <div className={`absolute bottom-0 right-0 w-96 h-96 ${darkMode ? 'bg-orange-600/5' : 'bg-orange-400/10'} blur-[120px] rounded-full`} />
      </div>

      {/* HEADER */}
      <header className={`h-20 shrink-0 flex items-center justify-between px-6 z-50 border-b backdrop-blur-xl transition-all duration-500 ${
        darkMode ? 'border-white/5 bg-black/40' : 'border-slate-200 bg-white/80'
      }`}>
        
        {/* LEFT: Back + Title */}
        <div className="flex items-center gap-4 min-w-[280px]">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/dashboard')}
            className={`p-2 rounded-xl border transition-all ${
              darkMode 
                ? 'border-white/10 hover:border-blue-500/30 hover:bg-blue-500/10' 
                : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
            }`}
          >
            <ChevronLeft size={20} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
          </motion.button>
          
          <div className="flex flex-col">
            <span className={`text-[9px] font-mono uppercase tracking-[0.3em] ${
              darkMode ? 'text-blue-400/60' : 'text-blue-600/70'
            }`}>
              Investigation Active
            </span>
            <h1 className={`text-sm font-black uppercase tracking-tight ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}>
              {courseData?.nom || "Chargement..."}
            </h1>
          </div>
        </div>

        {/* CENTER: SignalVisualizer + Progress - REMIS */}
        <div className="hidden xl:flex items-center gap-8 px-8 border-x border-white/5 h-full">
          <div className="flex flex-col items-center gap-2">
            <span className={`text-[8px] font-mono uppercase tracking-widest ${
              darkMode ? 'text-blue-400/60' : 'text-blue-600/70'
            }`}>
              Neural Sync
            </span>
            <div className="h-10 w-48">
              <SignalVisualizer isActive={true} height={40} />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[8px] font-mono uppercase tracking-widest ${
                darkMode ? 'text-slate-500' : 'text-slate-600'
              }`}>
                Completion
              </span>
              <span className={`text-[10px] font-black font-mono ${
                darkMode ? 'text-blue-400' : 'text-blue-600'
              }`}>
                {progress || 0}%
              </span>
            </div>
            <div className={`w-40 h-1.5 rounded-full overflow-hidden ${
              darkMode ? 'bg-white/5' : 'bg-slate-200'
            }`}>
              <motion.div 
                className="h-full bg-gradient-to-r from-blue-500 to-orange-500 shadow-[0 0 8px rgba(59,130,246,0.5)]"
                initial={{ width: 0 }}
                animate={{ width: `${progress || 0}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
          </div>
        </div>

        {/* RIGHT: Actions */}
        <div className="flex items-center gap-3 min-w-[280px] justify-end">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleShowNarrator}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
              showNarrator 
                ? darkMode
                  ? 'bg-blue-600 border-blue-500 text-white shadow-[0 0 15px rgba(59,130,246,0.4)]' 
                  : 'bg-blue-600 border-blue-500 text-white'
                : darkMode 
                  ? 'border-white/10 hover:border-blue-500/30 hover:bg-blue-500/10 text-blue-400' 
                  : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-blue-600'
            }`}
          >
            <Activity size={14} className={showNarrator ? '' : 'animate-pulse'} />
            <span className="text-[9px] font-black uppercase tracking-widest">Assistant</span>
          </motion.button>         
          
          
        </div>
      </header>

      <WorkspaceHeader isNavOpen={isNavOpen} isAssetsOpen={isAssetsOpen} />

      {/* MAIN CONTENT */}
      <main className="flex-1 flex gap-4 p-4 relative z-10 overflow-hidden">

        {/* SIDEBAR LEFT */}
        <motion.aside 
          initial={false}
          animate={{ 
            width: !ui.sidebarExpanded ? 0 : (isNavOpen ? 280 : 64),
            opacity: !ui.sidebarExpanded ? 0 : 1,
            marginRight: !ui.sidebarExpanded ? -16 : 0
          }}
          className="shrink-0 flex flex-col h-full relative overflow-hidden"
        >
          <div className={`h-full rounded-3xl border overflow-hidden flex flex-col backdrop-blur-xl transition-all duration-500 ${
            darkMode ? 'bg-black/40 border-white/5' : 'bg-white/90 border-slate-200'
          }`}>
            {ui.sidebarExpanded && (
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Database size={14} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                  {isNavOpen && (
                    <span className={`text-[11px] font-black uppercase tracking-widest ${
                      darkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      Modules
                    </span>
                  )}
                </div>
                <button 
                  onClick={() => setIsNavOpen(!isNavOpen)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    darkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'
                  }`}
                >
                  <Layers size={14} className={darkMode ? 'text-slate-400' : 'text-slate-600'} />
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto no-scrollbar">
              <SidebarNavigation isCollapsed={!isNavOpen} />
            </div>
          </div>
        </motion.aside>

        {/* CENTER: Learning Area */}
        <section className="flex-1 min-w-0 h-full flex flex-col relative">          
          
          {activeItem?.type !== 'capstone' && (
          <NeuralNarrator 
            isOpen={showNarrator} 
            onClose={closeNarrator}
            mediators={currentMediators}
            resolveAsset={resolveAsset}
          />
        )}

          <div className={`flex-1 rounded-3xl border flex flex-col overflow-hidden backdrop-blur-xl transition-all duration-500 ${
            darkMode ? 'bg-black/40 border-white/5' : 'bg-white/90 border-slate-200'
          }`}>
            <div className="flex-1 overflow-hidden relative">
              <LearningArea />
            </div>
          </div>
        </section>

        {/* SIDEBAR RIGHT: Resources */}
        <ResourcePanel 
          isOpen={isAssetsOpen} 
          setIsOpen={setIsAssetsOpen}
          currentModule={currentModule}
          isVisible={ui.activePanel !== 'none'}
          setEmbeddedMedia={setEmbeddedMedia} 
        />
        
        <AssetOverlay />
      </main>

      {/* FLOATING BACK BUTTON */}
      <AnimatePresence>
        {!ui.sidebarExpanded && (
          <motion.button
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            onClick={() => setCapstoneFocus(false)}
            className={`fixed left-0 top-1/2 -translate-y-1/2 z-[9000] pl-3 pr-2 py-4 rounded-r-2xl border border-l-0 shadow-xl transition-all group ${
              darkMode 
                ? 'bg-black/80 border-blue-500/30 text-blue-400 hover:bg-blue-600 hover:text-white' 
                : 'bg-white/90 border-slate-300 text-blue-600 hover:bg-blue-50'
            } backdrop-blur-md`}
          >
            <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

const SystemBootLoader = ({ darkMode }) => (
  <div className={`h-screen w-screen ${darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50'} flex flex-col items-center justify-center font-mono`}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      className={`w-16 h-16 border-2 ${darkMode ? 'border-white/10 border-t-blue-500' : 'border-slate-200 border-t-blue-600'} rounded-full mb-6`}
    />
    <span className={`text-[9px] tracking-[0.5em] uppercase animate-pulse ${
      darkMode ? 'text-blue-400' : 'text-blue-600'
    }`}>
      Initialisation Système
    </span>
  </div>
);

export default ParcoursPage;